/**
 * Générateur — Automatismes : partager une somme en deux parts selon un
 * certain ratio (1249) ; partager une masse en trois parts selon un certain
 * ratio (1250) ; partager une somme entre deux personnes proportionnellement
 * à leur âge (1251).
 * Attendu ID : 1249 | Niveau : 3ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 19, lignes 1386-1388 :
 * l. 1388 « deux personnes âgées de 20 et 30 ans », l'âge est ici la
 * variable, 20 et 30 la fiche).
 *
 * ── UN FICHIER, TROIS JEUX DE CLÉS (modèle 1101/1105) ─────────────────────
 * 1249 (clés deux_*), 1250 (clés trois_*, gen_1250), 1251 (clés age_*,
 * gen_1251) : le même geste (le total divisé par la somme des parts, puis
 * chaque part multipliée), sur deux parts, trois parts, ou des âges. Le
 * RATIO est le neuf du programme 2025 (objectif 4e l. 1364-1365 sans gen,
 * automatisé en 3e) : aucune activité MathsMentales, bornes du programme et
 * de gen_218 (3e objectif « Partager dans un ratio donné »). Cadrage
 * programme-maths 2026-09-12 (mode auto) : 1249 a, b 1..5, a ≠ b, premiers
 * entre eux, a = 1 un quart, T = (a + b) × k ≤ 1 000 € ; 1250 a, b, c 1..5,
 * pgcd 1, deux égaux admis, T ≤ 1 000 g ; 1251 âges 8..16 distincts, T
 * multiple de leur somme, ≤ 1 000 €. Hors : « x fois plus » (218), le ratio
 * depuis les parts (l. 1364), le partage additif (1225).
 *
 * ── Plan de cartes (par attendu : flash, QCM, Vrai/Faux) ─────────────────
 *   « Série flash » : deux lignes à plusieurs trous « 100 € partagés dans
 *     le ratio 2 : 3 : ⬚ € et ⬚ €. ».
 *   « Quelle part ? » : QCM sur UNE part nommée (la plus petite, la plus
 *     grande, la part de la personne de 12 ans).
 *   « Vrai ou faux » : quatre lignes « … dans le ratio 2 : 3 donnent 40 € et 60 €. » ;
 *   trois parts « 20 g ; 40 g ; 60 g » (jamais de virgule entre deux nombres) ;
 *   âges « …, proportionnellement à leur âge. Léa reçoit 120 € et Tom reçoit 130 €. »
 *   (retour prof 13/09 : ratio en gras, pas de virgule, deux lignes pour les âges).
 *
 * INVARIANTS : les parts re-calculées et re-sommées (somme = total, throw) ;
 * distracteurs distincts de la clé et entre eux, jamais deux fois la même
 * valeur (« 50 € double »).
 * SACRIFIÉS (fiche : la barre en parts de 1046/1049) : (100 € ; 2 : 3) et
 * (600 g ; 1 : 2 : 3) ; 1251 : (250 € ; 20 et 30 ans) hors plage, mais ses
 * parts (100 € ; 150 €) sur 250 € sont sacrifiées (10 et 15 ans les
 * donneraient) ; auto-testés par gen1249-1251.self.test.js et cdp3e-lot5.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   une_part_seule      : k, la valeur d'une part du ratio (1249, PRINCIPALE) ;
 *   parts_egales        : T ÷ 2, T ÷ 3 (1250/1251, PRINCIPALE) ;
 *   divise_par_le_terme : T ÷ a ou T ÷ b ;
 *   parts_echangees     : l'autre part ;
 *   ratio_recopie       : a × 10, le nombre du ratio pris pour la part ;
 *   age_pris_pour_part  : l'âge pris pour la part (1251).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1249;
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quelle part ?';
const L_VF = 'Vrai ou faux';

const PRENOMS = ['Léa', 'Tom', 'Inès', 'Noah', 'Jade', 'Sami', 'Lina', 'Enzo'];
const pgcd = (u, v) => (v === 0 ? u : pgcd(v, u % v));

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les exemples des fiches. */
export const SACRIFIES = { deux: { total: 100, ratio: [2, 3] }, trois: { total: 600, ratio: [1, 2, 3] }, age: { total: 250, parts: [100, 150] } };
const memeRatio = (u, v) => u.length === v.length && u.every((x, i) => x === v[i]);
const estSacrifie = (type, total, ratio) => (type === 'deux' && total === SACRIFIES.deux.total && memeRatio(ratio, SACRIFIES.deux.ratio))
    || (type === 'trois' && total === SACRIFIES.trois.total && memeRatio(ratio, SACRIFIES.trois.ratio));

/** Les parts d'un total selon un ratio, re-sommées (throw). */
const partsDe = (total, ratio) => {
    const s = ratio.reduce((a, b) => a + b, 0);
    if (total % s !== 0) throw new Error(`gen_1249 : ${total} non multiple de ${s}`);
    const k = total / s;
    const parts = ratio.map((r) => r * k);
    if (parts.reduce((a, b) => a + b, 0) !== total) throw new Error('gen_1249 : somme des parts');
    return { k, parts };
};

/**
 * Un item { type, total, ratio, unite, k, parts, ages?, prenoms? } :
 *   deux : ratio a : b, a ≠ b premiers entre eux, a = 1 un quart, T ≤ 1 000 € ;
 *   trois : a : b : c, pgcd 1, T ≤ 1 000 g ;
 *   age : deux âges 8..16 distincts, T multiple de leur somme, ≤ 1 000 €.
 */
const tirer = (type, exclure = []) => {
    for (let essai = 0; essai < 300; essai++) {
        let ratio;
        if (type === 'deux') {
            const a = randInt(1, 4) === 1 ? 1 : randInt(2, 5);
            const b = randInt(1, 5);
            if (a === b || pgcd(a, b) !== 1) continue;
            ratio = [a, b];
        } else if (type === 'trois') {
            ratio = [randInt(1, 5), randInt(1, 5), randInt(1, 5)];
            if (pgcd(pgcd(ratio[0], ratio[1]), ratio[2]) !== 1 || new Set(ratio).size === 1) continue;
        } else {
            const a1 = randInt(8, 16); const a2 = randInt(8, 16);
            if (a1 === a2) continue;
            ratio = [a1, a2];
        }
        const s = ratio.reduce((a, b) => a + b, 0);
        const kMax = Math.floor(1000 / s);
        if (kMax < 2) continue;
        // une part « ronde » (5, 10, 20…) : le total se divise de tête
        const k = randomChoice((type === 'age' ? [5, 10, 10, 20] : [5, 10, 10, 20, 25, 50, 100]).filter((x) => x <= kMax));
        const total = k * s;
        if (total < 20 || estSacrifie(type, total, ratio) || exclure.includes(`${total}|${ratio.join(':')}`)) continue;
        const { parts } = partsDe(total, ratio);
        if (new Set(parts).size !== parts.length && type !== 'trois') continue;
        // la fiche 1251 (250 € entre 20 et 30 ans : 100 € et 150 €) : les mêmes parts ne sortent jamais (10 et 15 ans, coup de pouce 13/09)
        if (type === 'age' && total === SACRIFIES.age.total && memeRatio([...parts].sort((u, v) => u - v), SACRIFIES.age.parts)) continue;
        const it = { type, total, ratio, unite: type === 'trois' ? 'g' : '€', k, parts };
        if (type === 'age') { const [p1, p2] = shuffle([...PRENOMS]).slice(0, 2); it.ages = ratio; it.prenoms = [p1, p2]; }
        return it;
    }
    return type === 'deux' ? { type, total: 150, ratio: [1, 4], unite: '€', k: 30, parts: [30, 120] }
        : type === 'trois' ? { type, total: 900, ratio: [2, 3, 4], unite: 'g', k: 100, parts: [200, 300, 400] }
            : { type, total: 250, ratio: [12, 13], unite: '€', k: 10, parts: [120, 130], ages: [12, 13], prenoms: ['Léa', 'Tom'] };
};

/**
 * L'amorce : « 100 € partagés dans le ratio 2 : 3 » (le ratio en gras KaTeX, retour prof 13/09 : les deux-points
 * du ratio se perdaient dans le texte) / « 250 € partagés entre Léa (12 ans) et Tom (13 ans), proportionnellement à leur âge ».
 * Le ratio s'écrit SANS espace autour du deux-points dans la formule (retour prof 15/09) : le rendu « libellé : contenu »
 * du texte à trous coupe la ligne au premier « : » et cassait le $…$ en deux, affiché brut ; en mode maths, 2:3 et 2 : 3
 * se rendent exactement pareil, KaTeX pose lui-même l'espacement du deux-points.
 */
const amorce = (it) => (it.type === 'age'
    ? `${it.total} € partagés entre ${it.prenoms[0]} (${it.ages[0]} ans) et ${it.prenoms[1]} (${it.ages[1]} ans), proportionnellement à leur âge`
    : `${it.total} ${it.unite} partagés dans le ratio $\\mathbf{${it.ratio.join(':')}}$`);
/** « 40 € et 60 € » ; trois parts « 20 g ; 40 g ; 60 g » (jamais de virgule entre deux nombres) ; avec prénoms « Léa reçoit 120 € et Tom reçoit 130 € ». */
const listeParts = (parts, unite, prenoms = null) => {
    if (prenoms) return parts.map((p, i) => `${prenoms[i]} reçoit ${p} ${unite}`).join(' et ');
    const l = parts.map((p) => `${p} ${unite}`);
    return l.length === 2 ? l.join(' et ') : l.join(' ; ');
};
/** La suite de l'amorce dans une ligne : « donnent … » (ratio) ou « . Léa reçoit … » (âges). */
const suite = (it, texteParts) => (it.type === 'age' ? `. ${texteParts}` : ` donnent ${texteParts}`);

const EXPLICATIONS = {
    une_part_seule: 'Le ratio 2 : 3 fait 5 parts en tout : 100 ÷ 5 = 20 pour UNE part, puis 2 parts = 40 et 3 parts = 60.',
    parts_egales: 'Les parts ne sont pas égales : le ratio dit combien de parts chacun reçoit.',
    divise_par_le_terme: 'On divise le total par la SOMME des nombres du ratio (2 + 3 = 5), pas par l\'un d\'eux.',
    parts_echangees: 'Le premier nombre du ratio donne la première part : 2 parts sur 5, la plus petite.',
    ratio_recopie: 'Les nombres du ratio comptent des parts, ce ne sont pas les parts elles-mêmes.',
    age_pris_pour_part: 'L\'âge n\'est pas la part : il dit combien de parts chacun reçoit (12 parts et 13 parts sur 25).',
    regle: 'J\'ajoute les nombres du ratio (ou les âges), je divise le total par cette somme : c\'est une part. Puis je multiplie par le nombre de parts de chacun.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats pour la part d'indice `i`, entiers positifs, jamais la clé ni deux fois la même valeur. */
const candidats = (it, i) => {
    const { total, ratio, k, parts } = it;
    const c = [];
    const pousser = (code, v) => { if (Number.isInteger(v) && v > 0 && v !== parts[i] && !c.some((x) => x.v === v)) c.push({ code, v }); };
    pousser('une_part_seule', k);
    pousser('parts_egales', total / parts.length);
    ratio.forEach((r) => pousser('divise_par_le_terme', total / r));
    parts.forEach((p, j) => { if (j !== i) pousser('parts_echangees', p); });
    pousser('ratio_recopie', ratio[i] * 10);
    if (it.type === 'age') pousser('age_pris_pour_part', it.ages[i]);
    return c;
};
const distracteurs = (it, i) => {
    const pris = [];
    for (const x of shuffle(candidats(it, i))) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

const ATTENDU = { deux: 1249, trois: 1250, age: 1251 };

// ─── Les trois cartes, paramétrées par le type ────────────────────────────

const genFlash = (type) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [tirer(type)];
        items.push(tirer(type, [`${items[0].total}|${items[0].ratio.join(':')}`]));
        if (items[0].total === items[1].total && memeRatio(items[0].ratio, items[1].ratio)) continue;
        // ratio : « … dans le ratio 5 : 1 donnent ⬚ € et ⬚ €. » ; âges : deux lignes, l'amorce puis « Lina : ⬚ € ; Tom : ⬚ €. »
        const trous = (it) => (it.type === 'age'
            ? it.prenoms.map((n, i) => `${n} : {{${it.parts[i]}}} ${it.unite}`).join(' ; ')
            : it.parts.map((p) => `{{${p}}} ${it.unite}`).join(it.parts.length === 2 ? ' et ' : ' ; '));
        const phrase = items.map((it) => (it.type === 'age' ? `${amorce(it)}.\n${trous(it)}.` : `${amorce(it)} donnent ${trous(it)}.`)).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Complète.</b></p>', { ...opts(ATTENDU[type], L_FLASH), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, une_part_seule: EXPLICATIONS.une_part_seule } };
        payload._meta.params = { type, items };
        return payload;
    }
    throw new Error(`gen_1249 : série flash ${type} sans tirage`);
};

/** Le nom de la part demandée et son indice. */
const partDemandee = (it) => {
    if (it.type === 'age') { const i = randInt(0, 1); return { i, nom: `La part de ${it.prenoms[i]}` }; }
    const min = it.parts.indexOf(Math.min(...it.parts)); const max = it.parts.indexOf(Math.max(...it.parts));
    return randomChoice([{ i: min, nom: 'La plus petite part' }, { i: max, nom: 'La plus grande part' }]);
};

const genQcm = (type) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer(type);
        const { i, nom } = partDemandee(it);
        const faux = distracteurs(it, i);
        if (!faux) continue;
        const bon = String(it.parts[i]);
        const choix = [bon, ...faux.map((f) => String(f.v))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p>${amorce(it)}.</p><p><b>${nom} est… (en ${it.unite})</b></p>`, opts(ATTENDU[type], L_QCM));
        payload.contenu = { ...payload.contenu, sousType: type, explications: EXPLICATIONS };
        payload._meta.params = { ...it, partDemandee: i, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = tirer(type);
    const i = 0;
    const payload = makeQcm([String(it.parts[0]), String(it.k), String(it.total / it.parts.length), String(it.parts[1])].filter((v, j, arr) => arr.indexOf(v) === j).slice(0, 4), String(it.parts[0]), `<p>${amorce(it)}.</p><p><b>La première part est… (en ${it.unite})</b></p>`, opts(ATTENDU[type], L_QCM));
    payload.contenu = { ...payload.contenu, sousType: type, explications: EXPLICATIONS };
    payload._meta.params = { ...it, partDemandee: i, distracteurs: [] };
    return payload;
};

const genVf = (type) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer(type, items.map((it) => `${it.total}|${it.ratio.join(':')}`)));
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, j) => {
            if (verites[j]) return { ligne: `${amorce(it)}${suite(it, listeParts(it.parts, it.unite, it.prenoms))}.`, code: null };
            // une part fausse (une famille), les autres justes, ou les parts échangées
            const i = randInt(0, it.parts.length - 1);
            const cands = candidats(it, i).filter((x) => x.code !== 'parts_echangees');
            const echangees = it.parts.length === 2 && it.parts[0] !== it.parts[1] && randomChoice([true, false]);
            if (echangees) return { ligne: `${amorce(it)}${suite(it, listeParts([it.parts[1], it.parts[0]], it.unite, it.prenoms))}.`, code: 'parts_echangees' };
            if (!cands.length) return null;
            const x = randomChoice(cands);
            const parts = it.parts.map((p, j2) => (j2 === i ? x.v : p));
            return { ligne: `${amorce(it)}${suite(it, listeParts(parts, it.unite, it.prenoms))}.`, code: x.code };
        });
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(ATTENDU[type], L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { type, items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error(`gen_1249 : V/F ${type} sans tirage`);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'une part NOMMÉE (la plus petite, la plus grande, la part de Léa) ; quatre nombres distincts, jamais deux fois la même valeur',
        'totaux multiples de la somme des parts, ≤ 1 000 ; parts re-sommées (throw)',
        'sacrifiés : (100 € ; 2 : 3) et (600 g ; 1 : 2 : 3) ; 1251 hors plage (20 et 30 ans)',
    ],
    qcm_unique: 'une_part_seule (k, principale à deux parts), parts_egales (principale à trois parts et à l\'âge), divise_par_le_terme, parts_echangees, ratio_recopie, age_pris_pour_part',
    vf_tableau_vf: 'quatre partages distincts ; une ligne fausse porte une part d\'une famille, ou les parts échangées',
};

// ─── Registre et points d'entrée ──────────────────────────────────────────

const GENERATORS = {
    deux_flash_texte_trou_saisie: genFlash('deux'),
    deux_qcm_unique: genQcm('deux'),
    deux_tableau_vf: genVf('deux'),
    trois_flash_texte_trou_saisie: genFlash('trois'),
    trois_qcm_unique: genQcm('trois'),
    trois_tableau_vf: genVf('trois'),
    age_flash_texte_trou_saisie: genFlash('age'),
    age_qcm_unique: genQcm('age'),
    age_tableau_vf: genVf('age'),
};

const categories = (prefixe) => [
    { id: `${prefixe}_flash`, label: L_FLASH, formatsList: [{ key: `${prefixe}_flash_texte_trou_saisie`, label: 'Saisie trous' }] },
    { id: `${prefixe}_qcm`, label: L_QCM, formatsList: [{ key: `${prefixe}_qcm_unique`, label: 'QCM' }] },
    { id: `${prefixe}_vf`, label: L_VF, formatsList: [{ key: `${prefixe}_tableau_vf`, label: 'Vrai/Faux' }] },
];
/** 1249 : deux parts. */
export const CATEGORIES = categories('deux');
/** 1250 : trois parts (gen_1250). */
export const CATEGORIES_TROIS = categories('trois');
/** 1251 : proportionnellement à l'âge (gen_1251). */
export const CATEGORIES_AGE = categories('age');

export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

const CLES_1249 = Object.keys(GENERATORS).filter((k) => k.startsWith('deux_'));
export const generate = (typeExercice) => genererParmi(CLES_1249, typeExercice);
export { ATTENDU_ID };
